/* SpawnAnything — the hotbar: which item sits in which of the nine number-key slots (1–9) of the battle tool belt.
   Pure data + localStorage persistence (no DOM, testable in Node). The item window (src/ui/itemWindow.js) edits it,
   the tool belt and the digit keys follow it. A slot holds an item key (a tool key like 'granate', 'grab',
   'disaster_tornado') or null; every key sits in at most one slot. Locked crate tools may sit in a slot (they show
   locked and open the crate screen, as before); unknown keys (removed content, old saves) are dropped on load. */

export const SLOTS = 9;
export const HOTBAR_KEY = 'aw-hotbar';
/** the belt as it always was: hand, the free tools, the two crate tools last */
export const DEFAULT_HOTBAR = Object.freeze(['hand', 'granate', 'fass', 'meteor', 'katapult', 'artillerie', 'stinkwolke', 'atom', 'orbitallaser']);

const okSlot = i => Number.isInteger(i) && i >= 0 && i < SLOTS;

/** a fresh copy of the default bar */
export const defaultHotbar = () => DEFAULT_HOTBAR.slice();

/** any input → exactly SLOTS entries; unknown keys (isKnown false) and doubles become null (first one wins) */
export function normalize(bar, isKnown = () => true) {
  const out = new Array(SLOTS).fill(null), seen = new Set();
  if (!Array.isArray(bar)) return out;
  for (let i = 0; i < SLOTS; i++) {
    const k = bar[i];
    if (typeof k !== 'string' || !k || seen.has(k) || !isKnown(k)) continue;
    out[i] = k; seen.add(k);
  }
  return out;
}

/** put key into slot; if the key already sits elsewhere the two slots swap (the old slot gets what was here) */
export function assign(bar, slot, key) {
  const b = bar.slice(); if (!okSlot(slot)) return b;
  if (key == null) { b[slot] = null; return b; }
  const from = b.indexOf(key);
  if (from === slot) return b;
  if (from >= 0) b[from] = b[slot];
  b[slot] = key;
  return b;
}
/** swap two slots (drag one slot onto another) */
export function swap(bar, a, c) {
  const b = bar.slice(); if (!okSlot(a) || !okSlot(c) || a === c) return b;
  [b[a], b[c]] = [b[c], b[a]];
  return b;
}
/** empty a slot */
export const clear = (bar, slot) => assign(bar, slot, null);
/** slot index of a key, -1 if it is not on the bar */
export const slotOf = (bar, key) => bar.indexOf(key);
/** key on number key n (1..9), or null */
export const keyFor = (bar, n) => (n >= 1 && n <= SLOTS ? bar[n - 1] || null : null);
/** same contents in the same order */
export const sameBar = (a, b) => a.length === b.length && a.every((k, i) => k === b[i]);

function store(storage) { if (storage !== undefined) return storage; try { return globalThis.localStorage || null; } catch (e) { return null; } }
/** stored bar (normalized), or the default when nothing (valid) is stored */
export function loadHotbar(isKnown = () => true, storage) {
  const s = store(storage); if (!s) return normalize(defaultHotbar(), isKnown);
  let raw = null;
  try { raw = JSON.parse(s.getItem(HOTBAR_KEY) || 'null'); } catch (e) { raw = null; }
  return normalize(Array.isArray(raw) ? raw : defaultHotbar(), isKnown);
}
/** write the bar; false when storage is missing or full */
export function saveHotbar(bar, storage) {
  const s = store(storage); if (!s) return false;
  try { s.setItem(HOTBAR_KEY, JSON.stringify(normalize(bar))); return true; } catch (e) { return false; }
}

/** small stateful wrapper: { get(), assign, swap, clear, reset, set, keyFor, slotOf, subscribe(f) } — every change persists */
export function createHotbar({ isKnown = () => true, storage } = {}) {
  let bar = loadHotbar(isKnown, storage);
  const subs = new Set();
  const commit = b => {
    const n = normalize(b, isKnown);
    if (sameBar(n, bar)) return bar;
    bar = n; saveHotbar(bar, storage);
    for (const f of subs) f(bar.slice());
    return bar;
  };
  return {
    get: () => bar.slice(),
    assign: (slot, key) => (key != null && !isKnown(key) ? bar : commit(assign(bar, slot, key))), // unknown key: no change
    swap: (a, c) => commit(swap(bar, a, c)),
    clear: slot => commit(clear(bar, slot)),
    reset: () => commit(defaultHotbar()),
    set: b => commit(b),
    keyFor: n => keyFor(bar, n),
    slotOf: key => slotOf(bar, key),
    subscribe(f) { subs.add(f); return () => subs.delete(f); },
  };
}
